/* ============================================================
 * RVRN Collection Filter — Section JS
 *
 * 维度下拉选项 = 该 collection 商品在「维度绑定的商品 metafield」上的所有值去重集合
 *   （商品 metafield 值可含多个子值，用 grid section 配置的 delimiter 拆分，如 a|b|c）
 * 同维度多选 → OR 合并；跨维度 → AND。
 * 每个维度是下拉窗口：未选时 trigger 显示 Dimension title，选中后显示 title · N。
 * 实时计数 + 动态置灰无交集选项（本地基于全量商品 metafield 计算）。
 * 变更广播 rvrn:collection-filter-change，由 grid section 订阅刷新。
 *   detail 里的 init=true 表示「初始化回显」（内容来自 ?filter= 恢复、非用户操作），
 *   订阅方据此跳过「回到第 1 页」，避免把 URL 恢复出来的 ?rvrn_page 打掉。
 *
 * 数据来源：目标 collection（与 grid 共用），经 view=rvrn-grid 轻量端点分页拉全量 meta。
 * ============================================================ */

(function () {
  'use strict';

  const SECTION_CLASS = 'rvrn-collection-filter';
  const EVENT_NAME = 'rvrn:collection-filter-change';

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.' + SECTION_CLASS).forEach(function (el) {
      if (el.dataset.initialized) return;
      el.dataset.initialized = '1';
      try { new RVRNCollectionFilter(el); }
      catch (e) { console.error('RVRN Collection Filter init failed:', e); }
    });
  });

  function RVRNCollectionFilter(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId || root.id.replace('shopify-section-', '');
    this.router = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (this.router.length && this.router.charAt(this.router.length - 1) !== '/') this.router += '/';
    this.collectionHandle = root.dataset.collection || root.dataset.collectionHandle || '';
    // delimiter 由 Product Grid section 配置，同页直接读取
    const gridEl = document.querySelector('.rvrn-product-grid');
    this.delimiter = (gridEl && gridEl.dataset.tagDelimiter ? gridEl.dataset.tagDelimiter : '|').trim() || '|';

    this.dimensions = [];
    this._collectDimensions();

    this.allProducts = [];
    this.totalAll = 0;
    this.selected = {};
    this.dimensions.forEach(function (_, i) { this.selected[i] = new Set(); }, this);

    this.countText = root.querySelector('[data-count]');
    this.clearBtn = root.querySelector('[data-action="clear-all"]');
    this.activeWrap = root.querySelector('[data-active-filters]');
    this.hintEl = root.querySelector('[data-hint]');
    this.inquiryEl = root.querySelector('[data-inquiry]');

    // 维度网格列数：实际维度数（最多 4 列），低于 4 时宽度自适应
    const colCount = Math.min(this.dimensions.length, 4);
    const grid = root.querySelector('[data-dimensions]');
    if (grid) grid.style.setProperty('--rvrn-cf-cols', String(colCount));

    // 自动捕获同页含表单的 section，作为 inquiry 跳转目标
    this._initInquiry();

    this._bindEvents();
    this._loadMetaFromGrid();
  }

  RVRNCollectionFilter.prototype._collectDimensions = function () {
    const self = this;
    this.root.querySelectorAll('.rvrn-collection-filter__group').forEach(function (group, dimIdx) {
      const titleEl = group.querySelector('.rvrn-collection-filter__trigger-label');
      self.dimensions.push({
        el: group,
        trigger: group.querySelector('[data-trigger]'),
        triggerLabel: titleEl ? titleEl.textContent.trim() : ('Dimension ' + (dimIdx + 1)),
        metaKey: (group.dataset.metafieldKey || '').trim(),
        optionsWrap: group.querySelector('[data-options]'),
        options: [],
        open: false
      });
    });
  };

  RVRNCollectionFilter.prototype._splitValues = function (raw) {
    if (!raw) return [];
    return String(raw).split(this.delimiter).map(function (s) { return s.trim(); }).filter(Boolean);
  };

  // 把任意类型的 metafield 值（字符串 / 数组 / 对象 / 数字 / 布尔）递归提取为可显示的去重字符串数组。
  // 解决 object 类型 metafield（如 custom.construction 为 json/object）在 meta 中变成 [object Object] 的问题：
  // grid 端用 | json 原样序列化进 [data-grid-meta]，JS 端 JSON.parse 后得到真实对象，此处递归提取其字段值。
  RVRNCollectionFilter.prototype._flattenMetaValue = function (raw) {
    let result = [];
    if (raw === null || raw === undefined) {
      result = [];
    } else if (typeof raw === 'string') {
      result = this._splitValues(raw);
    } else if (typeof raw === 'number' || typeof raw === 'boolean') {
      result = [String(raw)];
    } else if (Array.isArray(raw)) {
      const out = [];
      raw.forEach(function (item) { self_flatten(out, item); });
      result = dedupe(out);
    } else if (typeof raw === 'object') {
      // 维度 metafield 为标准对象结构（如 {active, display_order, name}），优先取展示字段 name/title/label
      const named = raw.name != null ? raw.name : (raw.title != null ? raw.title : raw.label);
      if (named != null) {
        result = this._flattenMetaValue(named);
      } else {
        const out = [];
        Object.keys(raw).forEach(function (k) { self_flatten(out, raw[k]); });
        result = dedupe(out);
      }
    }
    return result;
  };

  // 内部递归辅助：把单个值压平追加到 out 数组
  function self_flatten(out, val) {
    if (val === null || val === undefined) return;
    if (typeof val === 'string') {
      val.split('|').map(function (s) { return s.trim(); }).filter(Boolean).forEach(function (s) { out.push(s); });
    } else if (typeof val === 'number' || typeof val === 'boolean') {
      out.push(String(val));
    } else if (Array.isArray(val)) {
      val.forEach(function (item) { self_flatten(out, item); });
    } else if (typeof val === 'object') {
      // metaobject / object：优先只取展示字段 name/title/label，忽略 active/display_order 等噪声字段
      var named = val.name != null ? val.name
        : (val.title != null ? val.title
        : (val.label != null ? val.label : null));
      if (named != null) {
        self_flatten(out, named);
      } else {
        Object.keys(val).forEach(function (k) { self_flatten(out, val[k]); });
      }
    }
  }

  function dedupe(arr) {
    const seen = {};
    const out = [];
    arr.forEach(function (v) {
      if (!seen[v]) { seen[v] = true; out.push(v); }
    });
    return out;
  }

  RVRNCollectionFilter.prototype._bindEvents = function () {
    const self = this;
    // 下拉窗口开关
    this.dimensions.forEach(function (dim) {
      if (dim.trigger) {
        dim.trigger.addEventListener('click', function () {
          self._toggle(dim);
        });
      }
    });
    // 选项勾选
    this.root.addEventListener('change', function (e) {
      const input = e.target.closest('.rvrn-collection-filter__input');
      if (!input) return;
      const group = input.closest('.rvrn-collection-filter__group');
      const dimIdx = self.dimensions.findIndex(function (d) { return d.el === group; });
      if (dimIdx < 0) return;
      if (input.checked) self.selected[dimIdx].add(input.dataset.tag);
      else self.selected[dimIdx].delete(input.dataset.tag);
      self._updateTriggerLabel(dimIdx);
      self._applyFilter();
    });
    if (this.clearBtn) this.clearBtn.addEventListener('click', function () { self._clearAll(); });
    // 点击外部关闭下拉
    document.addEventListener('click', function (e) {
      if (!self.root.contains(e.target)) {
        self.dimensions.forEach(function (d) { if (d.open) self._toggle(d, false); });
      }
    });
  };

  RVRNCollectionFilter.prototype._toggle = function (dim, force) {
    const willOpen = (typeof force === 'boolean') ? force : !dim.open;
    // 互斥：展开当前维度前，先收起其他所有维度
    if (willOpen) {
      this.dimensions.forEach(function (d) {
        if (d !== dim && d.open) {
          d.open = false;
          if (d.trigger) d.trigger.setAttribute('aria-expanded', 'false');
          const otherPop = d.el.querySelector('[data-options-pop]');
          if (otherPop) otherPop.hidden = true;
        }
      });
    }
    dim.open = willOpen;
    if (dim.trigger) dim.trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    const pop = dim.el.querySelector('[data-options-pop]');
    if (pop) pop.hidden = !willOpen;
  };

  RVRNCollectionFilter.prototype._updateTriggerLabel = function (dimIdx) {
    const dim = this.dimensions[dimIdx];
    if (!dim.trigger) return;
    const label = dim.trigger.querySelector('.rvrn-collection-filter__trigger-label');
    const n = this.selected[dimIdx].size;
    label.textContent = n > 0 ? (dim.triggerLabel + ' · ' + n) : dim.triggerLabel;
  };

  // 从同页 grid section 已渲染的 [data-grid-meta] 读取全量商品 meta
  // （与 grid 同源、含商品所有 metafield，维度 metafield key 任意可配）
  RVRNCollectionFilter.prototype._loadMetaFromGrid = function () {
    const gridEl = document.querySelector('.rvrn-product-grid');
    if (!gridEl) {
      if (this.countText) this.countText.textContent = 'No grid found';
      return;
    }
    const metaEls = gridEl.querySelectorAll('[data-grid-meta]');
    const collected = [];
    metaEls.forEach(function (el) {
      const json = el.textContent || el.innerHTML || '';
      if (!json) return;
      try { collected.push(JSON.parse(json)); } catch (_) {}
    });
    if (collected.length === 0) {
      if (this.countText) this.countText.textContent = 'No products';
      return;
    }
    this.allProducts = collected;
    this.totalAll = collected.length;
    // 汇总所有真实存在的 metafield key
    const keySet = {};
    collected.forEach(function (p) {
      if (p && p.mf) Object.keys(p.mf).forEach(function (k) { keySet[k] = true; });
    });
    const allKeys = Object.keys(keySet);

    // 一致性校验：本 section 各维度的 metaKey 必须都出现在 grid 已渲染的 meta 中。
    // grid 已自动合并后台「RVRN Collection Filter」全局维度 key，因此此处缺失只可能是
    // 全局维度 key 与 grid 抓取池未对齐，或商品未填写对应 metafield。
    this.dimensions.forEach(function (dim) {
      const exists = allKeys.indexOf(dim.metaKey) > -1;
      if (!exists) {
        // 维度 metaKey 未出现在 grid meta 中：该维度选项将为空，跳过。
      }
    });

    this._buildOptionsFromMeta();
    this._restoreFromURL();          // 从 ?filter=… 恢复选择（配合卡片链接携带参数）
    // 标记这一次广播是「初始化回显」（内容来自 URL 恢复），而不是用户操作：
    // grid 侧据此跳过「回到第 1 页」，否则刚恢复出来的 ?rvrn_page 会被打掉、退回到第一页。
    this._isInitSync = true;
    this._applyFilter();
    this._isInitSync = false;
  };

  /**
   * 从 URL 恢复筛选状态：?filter=tag1,tag2
   *
   * 为什么需要：本节的选项只写在 URL 里（_pushURL 用 history.replaceState），
   * 而卡片链接会把当前参数带到商品页（section-rvrn-product-grid.js#_syncCardLinks）。
   * 若这里不读回，从商品页返回 / 直接分享带参数的链接时筛选状态就丢了；
   * 更糟的是初始化时 _applyFilter() → _pushURL([]) 会把这个参数**删掉**，
   * 等于参数刚带上就被清空。
   *
   * 未知 tag 直接忽略（容忍过期/手改的链接）；必须早于 _applyFilter() 执行。
   */
  RVRNCollectionFilter.prototype._restoreFromURL = function () {
    let raw = '';
    try { raw = new URLSearchParams(window.location.search).get('filter') || ''; } catch (e) { return; }
    if (!raw) return;
    const tags = raw.split(',').map(function (t) { return t.trim(); }).filter(Boolean);
    if (!tags.length) return;
    const self = this;
    let restored = 0;
    this.dimensions.forEach(function (dim, i) {
      tags.forEach(function (tag) {
        const opt = dim.options.find(function (o) { return o.tag === tag; });
        if (opt && opt.input) {
          opt.input.checked = true;
          self.selected[i].add(tag);
          restored++;
        }
      });
      self._updateTriggerLabel(i);
    });
    if (restored) console.log('[RVRN Collection Filter] restored ' + restored + ' tag(s) from ?filter=');
  };

  // 依据 dimension 绑定的 metafield_key，从全量商品去重生成下拉选项
  RVRNCollectionFilter.prototype._buildOptionsFromMeta = function () {
    const self = this;
    this.dimensions.forEach(function (dim, dimIdx) {
      if (!dim.metaKey) return;
      const seen = {};
      const ordered = [];
      self.allProducts.forEach(function (p) {
        const raw = p.mf ? p.mf[dim.metaKey] : null;
        self._flattenMetaValue(raw).forEach(function (v) {
          if (!seen[v]) { seen[v] = true; ordered.push(v); }
        });
      });
      dim.options = ordered.map(function (v) {
        return { tag: v, display: v };
      });
      self._renderOptions(dim, dimIdx);
    });
  };

  RVRNCollectionFilter.prototype._renderOptions = function (dim, dimIdx) {
    if (!dim.optionsWrap) return;
    const self = this;
    dim.optionsWrap.innerHTML = dim.options.map(function (opt, i) {
      const id = 'rvrn-cf-' + self.sectionId + '-' + dimIdx + '-' + i;
      return '<label class="rvrn-collection-filter__option" data-option>' +
        '<input type="checkbox" class="rvrn-collection-filter__input" ' +
        'id="' + id + '" value="' + self._escapeAttr(opt.tag) + '" ' +
        'data-tag="' + self._escapeAttr(opt.tag) + '" />' +
        '<span class="rvrn-collection-filter__box" aria-hidden="true"></span>' +
        '<span class="rvrn-collection-filter__text">' + self._escapeHtml(opt.display) + '</span>' +
        '<span class="rvrn-collection-filter__badge" data-badge hidden></span>' +
        '</label>';
    }).join('');
    dim.options.forEach(function (opt, i) {
      const label = dim.optionsWrap.children[i];
      opt.el = label;
      opt.input = label.querySelector('.rvrn-collection-filter__input');
    });
  };

  RVRNCollectionFilter.prototype._applyFilter = function () {
    const selTagsByDim = this.dimensions.map(function (_, i) { return Array.from(this.selected[i]); }, this);
    const hasSelection = selTagsByDim.some(function (s) { return s.length > 0; });

    const matched = hasSelection ? this.allProducts.filter(function (p) {
      return this._productMatches(p, selTagsByDim, null);
    }, this) : this.allProducts;

    if (this.countText) {
      this.countText.textContent = hasSelection
        ? 'Filtered Results (' + matched.length + ')'
        : 'Showing All (' + this.totalAll + ' Designs)';
    }

    this._updateOptionAvailability(selTagsByDim);
    this._renderActiveFilters();
    this._updateHint(hasSelection);
    // 先写 URL 再广播：监听者（如 section-rvrn-product-grid.js#_syncCardLinks）会在
    // 事件回调里读取 location.search，顺序反了就会拿到上一次的参数。
    this._pushURL(selTagsByDim);
    this._broadcast(selTagsByDim, matched.length, hasSelection, this._isInitSync);
  };

  RVRNCollectionFilter.prototype._productMatches = function (product, selTagsByDim, dimensionToIgnore) {
    for (let i = 0; i < this.dimensions.length; i++) {
      if (i === dimensionToIgnore) continue;
      const sel = selTagsByDim[i];
      if (!sel || sel.length === 0) continue;
      const raw = product.mf ? product.mf[this.dimensions[i].metaKey] : null;
      const values = this._flattenMetaValue(raw);
      const hit = sel.some(function (t) { return values.indexOf(t) > -1; });
      if (!hit) return false;
    }
    return true;
  };

  RVRNCollectionFilter.prototype._updateOptionAvailability = function (selTagsByDim) {
    const self = this;
    this.dimensions.forEach(function (dim, dimIdx) {
      const base = self.allProducts.filter(function (p) {
        return self._productMatches(p, selTagsByDim, dimIdx);
      });
      dim.options.forEach(function (opt) {
        const count = base.filter(function (p) {
          const raw = p.mf ? p.mf[dim.metaKey] : null;
          return self._flattenMetaValue(raw).indexOf(opt.tag) > -1;
        }).length;
        const isSelected = self.selected[dimIdx].has(opt.tag);
        const disabled = !isSelected && count === 0;
        if (opt.el) {
          opt.el.classList.toggle('is-disabled', disabled);
          if (opt.input) opt.input.disabled = disabled;
        }
        const badge = opt.el ? opt.el.querySelector('[data-badge]') : null;
        if (badge) {
          if (count > 0) { badge.hidden = false; badge.textContent = '(' + count + ')'; }
          else { badge.hidden = true; }
        }
      });
    });
  };

  RVRNCollectionFilter.prototype._renderActiveFilters = function () {
    if (!this.activeWrap) return;
    const self = this;
    const chips = [];
    this.dimensions.forEach(function (_dim, dimIdx) {
      Array.from(self.selected[dimIdx]).forEach(function (tag) {
        chips.push(
          '<span class="rvrn-collection-filter__chip">' + self._escapeHtml(tag) +
          '<button type="button" class="rvrn-collection-filter__chip-x" ' +
          'data-action="remove" data-dim="' + dimIdx + '" data-tag="' + self._escapeAttr(tag) + '">×</button>' +
          '</span>'
        );
      });
    });
    if (chips.length === 0) {
      this.activeWrap.hidden = true;
      this.activeWrap.innerHTML = '';
      if (this.clearBtn) this.clearBtn.hidden = true;
      return;
    }
    this.activeWrap.hidden = false;
    this.activeWrap.innerHTML = chips.join('');
    this.activeWrap.querySelectorAll('[data-action="remove"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const dimIdx = parseInt(btn.dataset.dim, 10);
        const tag = btn.dataset.tag;
        self.selected[dimIdx].delete(tag);
        const dim = self.dimensions[dimIdx];
        const opt = dim.options.find(function (o) { return o.tag === tag; });
        if (opt && opt.input) opt.input.checked = false;
        self._updateTriggerLabel(dimIdx);
        self._applyFilter();
      });
    });
    if (this.clearBtn) this.clearBtn.hidden = false;
  };

  RVRNCollectionFilter.prototype._clearAll = function () {
    const self = this;
    this.dimensions.forEach(function (dim, i) {
      self.selected[i].clear();
      dim.options.forEach(function (opt) { if (opt.input) opt.input.checked = false; });
      self._updateTriggerLabel(i);
    });
    this._applyFilter();
  };

  // 自动捕获同页含有表单的 section，作为 inquiry 链接跳转目标
  RVRNCollectionFilter.prototype._initInquiry = function () {
    if (!this.inquiryEl) return;
    const text = (this.inquiryEl.textContent || '').trim();
    if (!text) { this.inquiryEl.hidden = true; return; }

    // 跳转到当前页面中指定锚点（名称由后台 form_container_name 设置，默认 containerform）。
    // 参考 jump 🐰🐰 (anchor.liquid)：填入目标元素/表格的锚点 id（不含 #），点击平滑滚动到该位置。
    // 兼容旧值：也接受 CSS 类名（不含点）作为回退。
    const anchorName = (this.root.dataset.formContainerName || 'containerform').trim();
    const target = anchorName
      ? (document.getElementById(anchorName) || document.querySelector('#' + anchorName) || document.querySelector('.' + anchorName))
      : null;

    if (!target) { this.inquiryEl.hidden = true; return; }
    this.inquiryEl.hidden = false;
    this.inquiryEl.addEventListener('click', function (e) {
      e.preventDefault();
      // 平滑滚动到目标容器顶部，并预留固定头部偏移
      const top = target.getBoundingClientRect().top + window.pageYOffset - 16;
      window.scrollTo({ top: top, behavior: 'smooth' });
      // 若有首个可聚焦输入，稍后聚焦
      const firstField = target.querySelector('input:not([type="hidden"]), textarea, select');
      if (firstField) {
        setTimeout(function () { try { firstField.focus({ preventScroll: true }); } catch (_) {} }, 450);
      }
    });
  };

  // 已选为空时展示底部引导文案，否则隐藏
  RVRNCollectionFilter.prototype._updateHint = function (hasSelection) {
    if (this.hintEl) this.hintEl.hidden = hasSelection;
  };

  RVRNCollectionFilter.prototype._broadcast = function (selTagsByDim, count, hasSelection, isInit) {
    const detail = {
      sectionId: this.sectionId,
      collection: this.collectionHandle,
      delimiter: this.delimiter,
      metaKeys: this.dimensions.map(function (d) { return d.metaKey; }),
      tags: selTagsByDim.reduce(function (a, s) { return a.concat(s); }, []),
      selByDim: selTagsByDim,
      count: count,
      hasSelection: hasSelection,
      // 初始化回显（内容来自 URL 恢复，不是用户操作）：订阅方不应据此重置页码
      init: !!isInit
    };
    const fire = function () {
      try { document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: detail })); }
      catch (e) { /* ignore */ }
    };
    // 初始化回显延后一拍再广播：两个 section 的脚本都是 defer，DOMContentLoaded
    // 回调按脚本在页面中的顺序执行 —— 本 section 若排在 grid 之前，同步广播会在
    // grid 注册监听之前发出、被直接丢掉，表现为「带 ?filter=… 返回时筛选没生效」。
    if (isInit) { setTimeout(fire, 0); return; }
    fire();
  };

  RVRNCollectionFilter.prototype._pushURL = function (selTagsByDim) {
    try {
      const params = new URLSearchParams(window.location.search);
      const flat = selTagsByDim.reduce(function (a, s) { return a.concat(s); }, []);
      if (flat.length) params.set('filter', flat.join(','));
      else params.delete('filter');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
    } catch (e) { /* ignore */ }
  };

  RVRNCollectionFilter.prototype._collectionUrl = function (handle, tags, page) {
    const base = this.router + 'collections/' + encodeURIComponent(handle);
    const params = new URLSearchParams();
    params.set('view', 'rvrn-grid');
    if (tags && tags.length) tags.forEach(function (t) { params.append('constraint', 'tag:' + t); });
    if (page && page > 1) params.set('page', String(page));
    return base + '?' + params.toString();
  };

  RVRNCollectionFilter.prototype._escapeHtml = function (str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  };
  RVRNCollectionFilter.prototype._escapeAttr = function (str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  };

})();
